import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import OrderCard from '../OrderCard';
import OrdersSkeleton from './OrdersSkeleton';

const OrdersList = ({
  orders,
  ordersError,
  ordersLoading,
  isFetching,
  pagination,
  selectedOrder,
  handleSelectOrder,
  handlePrevPage,
  handleNextPage,
  theme,
  invoiceError,
  invoiceLoading,
}) => {
  const renderOrderCards = () => {
    if (ordersError) {
      return (
        <Typography color="error" align="center" sx={{ py: 4 }}>
          Erro ao carregar pedidos. Por favor, tente novamente.
        </Typography>
      );
    }

    // Se está carregando a primeira vez (não é fetching de nova página)
    if (ordersLoading && !isFetching) {
      return <OrdersSkeleton count={10} />;
    }

    // Se está fazendo fetch de nova página, mostra skeleton mas menor
    if (isFetching && orders.length === 0) {
      return <OrdersSkeleton count={5} />;
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1.5, md: 2 },
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          position: "relative",
          p: { xs: 1, md: 1.5 },
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0,0,0,0.1)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255, 152, 0, 0.3)",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: "rgba(255, 152, 0, 0.5)",
            },
          },
        }}
      >
        {isFetching && orders.length > 0 && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height={3}
            sx={{ zIndex: 1 }}
          >
            <LinearProgress color="warning" />
          </Box>
        )}

        {orders.length === 0 && !isFetching ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            Nenhum pedido encontrado.
          </Typography>
        ) : (
          orders.map((order) => {
            const isSelected = selectedOrder?._id === order._id;
            const isCurrentOrder = selectedOrder?._id === order._id;
            return (
              <OrderCard
                key={order._id}
                order={order}
                isSelected={isSelected}
                onClick={() => handleSelectOrder(order)}
                theme={theme}
                hasError={isCurrentOrder && invoiceError}
                isLoading={isCurrentOrder && invoiceLoading}
              />
            );
          })
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: { xs: "auto", md: "calc(100vh - 200px)" },
        maxHeight: { xs: 500, sm: 600, md: "calc(100vh - 200px)" },
        mb: { xs: 3, md: 0 },
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRadius: 3,
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        overflow: "hidden",
        border: "1px solid rgba(255, 152, 0, 0.2)"
      }}
    >
      {pagination && pagination.total_pages > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: { xs: 1.5, md: 2 },
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            bgcolor: "grey.50"
          }}
        >
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={handlePrevPage}
            disabled={!pagination.has_prev || isFetching}
            sx={{ 
              minWidth: { xs: 70, md: 80 },
              opacity: isFetching ? 0.6 : 1
            }}
          >
            Anterior
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isFetching && (
              <CircularProgress size={16} color="warning" />
            )}
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                fontWeight: 500,
                fontSize: { xs: "0.75rem", md: "0.875rem" }
              }}
            >
              {pagination.current_page} de {pagination.total_pages}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={handleNextPage}
            disabled={!pagination.has_next || isFetching}
            sx={{ 
              minWidth: { xs: 70, md: 80 },
              opacity: isFetching ? 0.6 : 1
            }}
          >
            Próxima
          </Button>
        </Box>
      )}
      {renderOrderCards()}
    </Box>
  );
};

export default OrdersList;